import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Container, CPText } from "shared/components/base";
import CPButton from "shared/components/shared/CPButton/CPButton";
import CPFontAwesome from "shared/components/shared/CPFontAwesome/CPFontAwesome";
import CPPanel from "shared/components/shared/CPPanel/CPPanel";
import { useAdminDashboardStatsQuery, AuditLogEntry } from "data/admin/admin";

import "./AdminPageStyles.scss";

interface QuickAction {
  title: string;
  description: string;
  route: string;
  buttonLabel: string;
  icon: string;
}

const quickActions: QuickAction[] = [
  {
    title: "Pending approvals",
    description: "Review pending access requests and approve or deny users.",
    route: "/admin/pending-users",
    buttonLabel: "Open pending approvals",
    icon: "fa-solid fa-user-clock",
  },
  {
    title: "Manage users",
    description:
      "Review account activity, pending approvals, and follow-up actions.",
    route: "/admin/users",
    buttonLabel: "Open user management",
    icon: "fa-solid fa-users-gear",
  },
  {
    title: "Report center",
    description: "Manage business intelligence reports and resources.",
    route: "/admin/reports",
    buttonLabel: "Open reports",
    icon: "fa-solid fa-chart-column",
  },
  {
    title: "Roles and access",
    description: "Manage roles and access exceptions.",
    route: "/admin/roles",
    buttonLabel: "Open roles",
    icon: "fa-solid fa-key",
  },
];

const dotClassForEvent = (eventType: string | null): string => {
  if (!eventType) return "warning";
  const lower = eventType.toLowerCase();
  if (lower.includes("denied")) return "danger";
  if (lower.includes("created") || lower.includes("approved")) return "success";
  return "warning";
};

const formatRelativeTime = (timeStamp: string): string => {
  const date = new Date(timeStamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return "Yesterday";
  return `${diffDay}d ago`;
};

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: stats, isLoading } = useAdminDashboardStatsQuery();

  const statCards = [
    {
      label: "Active users",
      value: isLoading ? "—" : String(stats?.activeUsers ?? 0),
      icon: "fa-solid fa-users",
      placeholder: false,
    },
    {
      label: "Pending approvals",
      value: isLoading ? "—" : String(stats?.pendingApprovals ?? 0),
      icon: "fa-solid fa-user-clock",
      placeholder: false,
    },
    {
      label: "Audit events today",
      value: isLoading ? "—" : String(stats?.todayAuditEventCount ?? 0),
      icon: "fa-solid fa-shield-halved",
      placeholder: false,
    },
    {
      label: "Scheduled reports",
      value: "—",
      icon: "fa-solid fa-chart-column",
      placeholder: true,
    },
  ];

  return (
    <Container padding="2em" className="cp-admin-dashboard">
      <Box className="admin-page-header" justifyContent="space-between">
        <Box flexDirection="column" spacing={4}>
          <CPText textStyle="h4">Admin Dashboard</CPText>
        </Box>
      </Box>

      <div className="admin-metric-grid">
        {statCards.map((card) => (
          <div key={card.label} className="admin-metric-card">
            <div className="admin-metric-top">
              <CPText className="admin-metric-label">{card.label}</CPText>
              <div className="admin-metric-icon-wrap">
                <CPFontAwesome icon={card.icon} size={14} />
              </div>
            </div>
            <CPText className="admin-metric-value">{card.value}</CPText>
            {card.placeholder && (
              <CPText className="admin-metric-helper">Future item</CPText>
            )}
          </div>
        ))}
      </div>

      <div className="admin-panels-row">
        <CPPanel header="Quick actions" style={{ flex: "2 1 0" }}>
          <div className="admin-action-grid">
            {quickActions.map((action) => (
              <div key={action.title} className="admin-action-card">
                <div className="admin-action-icon-wrap">
                  <CPFontAwesome icon={action.icon} size={18} />
                </div>
                <CPText className="admin-action-title" bold>
                  {action.title}
                </CPText>
                <CPText className="admin-action-description">
                  {action.description}
                </CPText>
                <CPButton
                  fillMode="flat"
                  themeColor="primary"
                  onClick={() => navigate(action.route)}
                >
                  {action.buttonLabel} <i className="fa-solid fa-arrow-right" />
                </CPButton>
              </div>
            ))}
          </div>
        </CPPanel>

        <CPPanel
          header="Recent activity"
          style={{ flex: "1 1 0", minWidth: 0 }}
          actionBtns={
            <button className="admin-panel-menu-btn" title="More options">
              <i className="fa-solid fa-ellipsis" />
            </button>
          }
        >
          <div className="admin-activity-list">
            {isLoading && (
              <div className="admin-activity-row">
                <CPText className="admin-metric-helper">Loading…</CPText>
              </div>
            )}
            {!isLoading &&
              (stats?.recentActivity ?? []).map(
                (entry: AuditLogEntry, idx: number) => (
                  <div key={idx} className="admin-activity-row">
                    <span
                      className={`admin-activity-dot ${dotClassForEvent(entry.eventType)}`}
                    />
                    <div className="admin-activity-body">
                      <CPText bold className="admin-activity-title">
                        {entry.eventType ?? "System event"}
                      </CPText>
                      {entry.userName && (
                        <CPText className="admin-activity-detail">
                          {entry.userName}
                        </CPText>
                      )}
                      <CPText className="admin-activity-meta">
                        {formatRelativeTime(entry.timeStamp)}
                      </CPText>
                    </div>
                  </div>
                ),
              )}
            {!isLoading && (stats?.recentActivity ?? []).length === 0 && (
              <div className="admin-activity-row">
                <CPText className="admin-metric-helper">
                  No recent activity
                </CPText>
              </div>
            )}
          </div>
        </CPPanel>
      </div>
    </Container>
  );
};

export default AdminPage;
